import { useToast } from '@/components/ui/use-toast';
import { useAnalytics } from '@/features/analytics/hooks/use-analytics';
import { Dialog } from '@/features/dialog/dialog';
import { DialogForm } from '@/features/dialog/dialog-form';
import { DialogStateManager, useDialog } from '@/features/dialog/dialog-provider';
import type { DialogInnerProps } from '@/features/dialog/types';
import type { co_getplato_grid_sales_features_draft_create_Response } from '@/lib/grid/api';
import { useLocale } from '@/lib/hooks/use-locale';
import { api } from '@/utils/api';
import { useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { DraftOrderFormProps } from './draft-order-form';
import { DraftOrderForm } from './draft-order-form';

interface DraftOrderCreateDialogProps extends DraftOrderFormProps, DialogInnerProps {
    onSuccess: (data: co_getplato_grid_sales_features_draft_create_Response) => void;
}

export const DraftOrderCreateDialog = DialogStateManager.create(
    ({ customerId, storageId, onSuccess }: DraftOrderCreateDialogProps) => {
        const dialog = useDialog();
        const { t } = useLocale();
        const { toast } = useToast();

        const sendEvent = useAnalytics();

        useEffect(() => {
            sendEvent('transaction_creation_started');
        }, [sendEvent]);

        const { mutate, isLoading } = api.platoUser.command.salesDraft.draft.create.useMutation({
            onSuccess(data) {
                onSuccess(data);
                dialog.remove();
            },
            onError(error) {
                console.error('onError', error);

                toast({
                    title: 'error',
                    description: error.message,
                    icon: 'warning',
                });
            },
        });

        return (
            <Dialog open={dialog.visible} onClose={dialog.remove} showClose>
                <DialogForm className="max-w-2xl">
                    <DialogForm.Header title={t('sales_draft__new_form___title')} />
                    <DraftOrderForm
                        customerId={customerId}
                        storageId={storageId}
                        onSubmit={(data) => {
                            mutate({
                                draft_order_id: uuidv4(),
                                ...data,
                            });
                        }}
                        onCancel={dialog.remove}
                        isLoading={isLoading}
                    />
                </DialogForm>
            </Dialog>
        );
    },
);
